import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const Hero: React.FC = () => {
  const heroRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);

  const togglePlayback = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };

  // Lock height to initial viewport height on mobile (prevents Instagram browser jumping)
  useEffect(() => {
    if (window.innerWidth >= 768) return; // Desktop only uses CSS

    // Get locked height - try CSS variable first, fallback to direct measurement
    const getLockedHeight = () => {
      const cssHeight = document.documentElement.style.getPropertyValue('--vh-px');
      if (cssHeight) return parseFloat(cssHeight);
      // Fallback: use initial window height (when address bar is visible)
      return window.innerHeight;
    };

    const lockedHeight = getLockedHeight();
    if (heroRef.current && lockedHeight) {
      // Set fixed height ONCE and never change it
      heroRef.current.style.height = `${lockedHeight}px`;
      heroRef.current.style.maxHeight = `${lockedHeight}px`;
    }
  }, []);

  // Force autoplay for iOS
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.play().catch(error => {
        console.log("Autoplay prevented:", error);
      });
    }
  }, []);

  return (
    <section
      ref={heroRef}
      id="hero"
      className="relative w-full h-screen-fixed md:h-screen flex flex-col justify-center items-start overflow-hidden bg-black"
      style={{
        isolation: 'isolate',
        // Pull the section up behind the safe area padding on body
        marginTop: 'calc(-1 * env(safe-area-inset-top, 0px))',
        // Add extra height to compensate for the negative margin
        paddingTop: 'env(safe-area-inset-top, 0px)'
      }}
      aria-label="Hero section"
    >
      {/* Video Background - Structural Isolation Layer */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none select-none" aria-hidden="true">
        {/* Double-masking wrapper for Safari rendering stability */}
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              WebkitTransform: 'scale(1.02)', // Slight over-scale to hide sub-pixel gaps in Safari
              transform: 'scale(1.02)'
            }}
            onLoadedMetadata={() => {
              if (videoRef.current && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                videoRef.current.pause();
              }
            }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          >
            <source src="/hero-video-new.mp4" type="video/mp4" />
          </video>
        </div>
        {/* Darken overlay */}
        <div className="absolute inset-0 bg-black/30 z-10" />
      </div>

      {/* Main Content - Hardware Accelerated Layer */}
      <div
        className="relative z-20 flex flex-col items-start h-full text-left px-6 md:px-16 lg:px-24 pt-[35vh] w-full max-w-full"
        style={{ WebkitTransform: 'translate3d(0,0,0)', transform: 'translate3d(0,0,0)' }}
      >
        {/* Headline */}
        <h1
          id="hero-logo"
          className="text-5xl md:text-7xl lg:text-8xl font-normal tracking-[-0.02em] text-white leading-[1.1] max-w-5xl break-words"
        >
          Take control of your cognitive performance
        </h1>

        {/* Subheadline */}
        <p className="mt-6 md:mt-8 ml-[3px] text-lg md:text-xl text-white leading-relaxed text-balance max-w-2xl">
          Your best work happens when your mind is clear.<br />
          Maintain that state every day with Nura.
        </p>

        {/* CTA Button */}
        <Link
          to="/app"
          className="mt-6 md:mt-8 ml-[3px] self-start min-w-[108px] text-center text-lg text-white whitespace-nowrap bg-transparent border border-white px-5 py-2 rounded-full hover:bg-white hover:text-black transition-colors focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-4 focus:outline-none"
        >
          Try Now
        </Link>
      </div>

      {/* Manual Play/Pause Toggle - Minimal style */}
      <button
        onClick={togglePlayback}
        className="absolute bottom-8 right-4 md:bottom-12 md:right-12 z-30 p-2 text-white opacity-80 hover:opacity-60 transition-opacity cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-full group"
        aria-label={isPlaying ? "Pause background video" : "Play background video"}
      >
        {isPlaying ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="drop-shadow-sm">
            <rect x="6" y="4" width="4" height="16" rx="1.5"></rect>
            <rect x="14" y="4" width="4" height="16" rx="1.5"></rect>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="drop-shadow-sm ml-0.5">
            <path d="M5 3l14 9-14 9V3z"></path>
          </svg>
        )}
      </button>
    </section>
  );
};

export default Hero;
